// The pieces every Stock dialog shares (spec/13 §6.4): an item's level as a word with its tint, the item picker —
// search by name, SKU or barcode, where a scanner's Enter resolves the barcode to its item and pack — and the quantity
// entry — packs ("Case of 24": 2) and loose units in the natural unit (kg or g, L or ml, pieces) — that every
// delivery, count, waste entry and recipe uses, so a quantity is typed the same way everywhere.

import type { StockCountEntry, StockItem, StockItemPack, StockLevel } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Input, Select } from '@clavian/ui-web';
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { SearchField } from '../../ui';
import { LEVEL_TONE, useStockFormat } from './format';
import { useLookupBarcode } from './queries';
import { inputTextOf, inputUnitsFor, naturalInputUnit, parseQuantity, totalOf, type DisplayUnit } from './units';

/** An item's state as a word with its tint — never colour alone. */
export function LevelBadge({ level }: { readonly level: StockLevel }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Badge tone={LEVEL_TONE[level]} dot={level !== 'ok'}>
      {t(`stock.level.${level}`)}
    </Badge>
  );
}

/** The item's packs a person can still pick (an archived one is history only). */
export const activePacks = (item: Pick<StockItem, 'packs'>): readonly StockItemPack[] => item.packs.filter((pack) => !pack.archived);

/** A barcode as scanners type it: eight to fourteen digits, then Enter. */
const looksLikeBarcode = (text: string) => /^\d{8,14}$/.test(text.trim());

// -------------------------------------------------------------------------------------------------
// Picking an item
// -------------------------------------------------------------------------------------------------

export interface ItemPickerProps {
  readonly organizationId: string;
  readonly venueId: string;
  readonly items: readonly StockItem[];
  /** A pick: the item, and the pack whose barcode was scanned (null for a typed pick or the item's own barcode). */
  readonly onPick: (item: StockItem, pack: StockItemPack | null) => void;
  readonly label: string;
  /** Items already chosen, left out of the results. */
  readonly exclude?: ReadonlySet<string>;
  /** Only these items may be picked (supplies only, for a member who handles supplies). */
  readonly allow?: (item: StockItem) => boolean;
  readonly autoFocus?: boolean;
}

/**
 * Search by name, SKU or barcode. Typing lists the matches under the field; Enter picks the only match, or — for a
 * scanned barcode — asks the server which item (and pack) it names, so a case's barcode picks the case.
 */
export function ItemPicker({ organizationId, venueId, items, onPick, label, exclude, allow, autoFocus = false }: ItemPickerProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useStockFormat();
  const lookup = useLookupBarcode(organizationId, venueId);
  const [q, setQ] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const resultsId = useId();
  const needle = q.trim().toLowerCase();
  const pickable = items.filter((item) => !item.archived && !(exclude?.has(item.id) ?? false) && (allow?.(item) ?? true));
  const results =
    needle === ''
      ? []
      : pickable
          .filter((item) => [item.name, item.sku ?? '', item.barcode ?? '', ...item.packs.map((pack) => pack.barcode ?? '')].some((text) => text.toLowerCase().includes(needle)))
          .slice(0, 8);

  const pick = (item: StockItem, pack: StockItemPack | null) => {
    onPick(item, pack);
    setQ('');
    setMessage(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const text = q.trim();
    if (text === '') return;
    // A scan: the server knows a pack's barcode and a retail product's as well as the item's own.
    if (looksLikeBarcode(text)) {
      lookup.mutate(text, {
        onSuccess: (found) => {
          const item = pickable.find((one) => one.id === found.item.id);
          if (item === undefined) {
            setMessage(exclude?.has(found.item.id) === true ? t('stock.picker.already', { name: found.item.name }) : t('stock.picker.notAllowed', { name: found.item.name }));
            return;
          }
          pick(item, found.pack);
        },
        onError: (error) => setMessage(problemCode(error) === PROBLEM.notFound ? t('stock.picker.unknownBarcode', { barcode: text }) : problem.text(error, { fallback: t('stock.picker.lookupFailed') })),
      });
      return;
    }
    const only = results.length === 1 ? results[0] : undefined;
    if (only !== undefined) pick(only, null);
  };

  return (
    <div className="console-stock-pick">
      <SearchField
        label={label}
        placeholder={t('stock.picker.placeholder')}
        value={q}
        onChange={(event) => {
          setQ(event.target.value);
          setMessage(null);
        }}
        onKeyDown={onKeyDown}
        aria-controls={resultsId}
        // The field a counter's scanner types into when a dialog opens on it.
        autoFocus={autoFocus}
      />
      {message !== null ? (
        <p className="console-stock-pick__message" role="status">
          {message}
        </p>
      ) : null}
      {needle !== '' && results.length === 0 && message === null && !looksLikeBarcode(q) ? <p className="console-subtle">{t('stock.picker.noMatch')}</p> : null}
      <ul className="console-stock-pick__results" id={resultsId} aria-label={t('stock.picker.results')}>
        {results.map((item) => (
          <li key={item.id}>
            <button type="button" className="console-stock-pick__result" onClick={() => pick(item, null)}>
              <span className="console-stock-pick__name">{item.name}</span>
              <span className="console-subtle">{t(`stock.kind.${item.kind}`)}</span>
              <span className="console-stock-pick__onhand console-num">{format.quantity(item.onHand, item)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Typing a quantity: packs and loose units
// -------------------------------------------------------------------------------------------------

/** A quantity as typed: a count per pack, and a loose amount in a display unit. */
export interface QuantityEntry {
  readonly packs: Readonly<Record<string, string>>;
  readonly loose: string;
  readonly unit: DisplayUnit;
}

/** A blank entry for an item, typed in its natural unit; `pack` starts at one of that pack (a scanned case). */
export function blankEntry(item: StockItem, pack: StockItemPack | null = null): QuantityEntry {
  const typical = [...item.packs.map((one) => one.quantity), ...(item.parLevel === null ? [] : [item.parLevel])];
  return { packs: pack === null ? {} : { [pack.id]: '1' }, loose: '', unit: naturalInputUnit(item.baseUnit, typical) };
}

/**
 * An entry that starts at a count's figure already entered: as it was typed — so many packs and the loose units —
 * when the count kept that and the item's packs are still the same size; otherwise the total, loose in the natural unit.
 */
export function entryOf(item: StockItem, quantity: number | null, entered: StockCountEntry | null = null): QuantityEntry {
  const blank = blankEntry(item);
  if (quantity === null) return blank;
  const packs = activePacks(item);
  const typed = entered?.packs.filter((pack) => pack.count > 0) ?? [];
  const same = typed.every((pack) => packs.some((one) => one.id === pack.packId && one.quantity === pack.quantity));
  if (entered === null || typed.length === 0 || !same) return { ...blank, loose: inputTextOf(quantity, blank.unit) };
  return {
    ...blank,
    packs: Object.fromEntries(typed.map((pack) => [pack.packId, String(pack.count)])),
    loose: entered.quantity === 0 ? '' : inputTextOf(entered.quantity, blank.unit),
  };
}

/** What an entry comes to in base units: `null` when nothing is typed, `'invalid'` when a figure is not a quantity. */
export function entryTotal(entry: QuantityEntry, item: Pick<StockItem, 'packs'>): number | null | 'invalid' {
  const packs: { size: number; count: number }[] = [];
  let typed = false;
  for (const pack of activePacks(item)) {
    const text = (entry.packs[pack.id] ?? '').trim();
    if (text === '') continue;
    typed = true;
    if (!/^\d+$/.test(text)) return 'invalid';
    packs.push({ size: pack.quantity, count: Number(text) });
  }
  let loose = 0;
  if (entry.loose.trim() !== '') {
    typed = true;
    const parsed = parseQuantity(entry.loose, entry.unit);
    if (parsed === null) return 'invalid';
    loose = parsed;
  }
  if (!typed) return null;
  return totalOf(packs, loose) ?? 'invalid';
}

/** The entry as the wire takes it: loose base units and a count per pack. */
export function entryToWire(entry: QuantityEntry, item: Pick<StockItem, 'packs'>): { quantity?: number; packs?: { packId: string; count: number }[] } {
  const packs = activePacks(item)
    .map((pack) => ({ packId: pack.id, count: Number((entry.packs[pack.id] ?? '').trim() || '0') }))
    .filter((pack) => pack.count > 0);
  const loose = entry.loose.trim() === '' ? 0 : (parseQuantity(entry.loose, entry.unit) ?? 0);
  return { ...(loose > 0 || packs.length === 0 ? { quantity: loose } : {}), ...(packs.length === 0 ? {} : { packs }) };
}

/** The one pack an entry is made of, alone (for a cost per pack), or null when it mixes packs or loose units. */
export function onlyPackOf(entry: QuantityEntry, item: Pick<StockItem, 'packs'>): { pack: StockItemPack; count: number } | null {
  if (entry.loose.trim() !== '') return null;
  const used = activePacks(item).filter((pack) => (entry.packs[pack.id] ?? '').trim() !== '' && (entry.packs[pack.id] ?? '').trim() !== '0');
  const only = used.length === 1 ? used[0] : undefined;
  if (only === undefined) return null;
  const count = Number(entry.packs[only.id]);
  return Number.isSafeInteger(count) && count > 0 ? { pack: only, count } : null;
}

export interface QuantityFieldsProps {
  readonly item: StockItem;
  readonly entry: QuantityEntry;
  readonly onChange: (entry: QuantityEntry) => void;
  /** Names the inputs for a screen reader: "Frozen fries". */
  readonly label: string;
  readonly error?: string | undefined;
  /** `compact` for a counting row: the packs and the loose field in one line, no total under them. */
  readonly compact?: boolean;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly onBlur?: () => void;
  /** A ref-like callback for the first input, so a counting sheet can move focus row to row. */
  readonly firstInputRef?: (element: HTMLInputElement | null) => void;
  readonly disabled?: boolean;
}

/**
 * One input per pack ("2.5 kg bag") and a loose amount with its unit (kg or g, L or ml, pieces), and what it all
 * comes to under them ("= 5.5 kg").
 */
export function QuantityFields({ item, entry, onChange, label, error, compact = false, onKeyDown, onBlur, firstInputRef, disabled = false }: QuantityFieldsProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useStockFormat();
  const errorId = useId();
  const packs = activePacks(item);
  const units = inputUnitsFor(item.baseUnit);
  const total = entryTotal(entry, item);
  const invalid = total === 'invalid' || error !== undefined;
  const describedBy = invalid ? errorId : undefined;

  return (
    <div className={compact ? 'console-stock-qty console-stock-qty--compact' : 'console-stock-qty'} role="group" aria-label={t('stock.quantity.groupLabel', { name: label })}>
      <div className="console-stock-qty__inputs">
        {packs.map((pack, index) => (
          <label key={pack.id} className="console-stock-qty__field">
            <span className="console-stock-qty__label">
              {pack.name}
              <span className="console-subtle"> {t('stock.quantity.packSize', { size: format.quantity(pack.quantity, item) })}</span>
            </span>
            <Input
              ref={index === 0 ? firstInputRef : undefined}
              numeric
              inputMode="numeric"
              className="console-stock-qty__input"
              aria-label={t('stock.quantity.packsOf', { pack: pack.name, name: label })}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              value={entry.packs[pack.id] ?? ''}
              placeholder="0"
              disabled={disabled}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onChange={(event) => onChange({ ...entry, packs: { ...entry.packs, [pack.id]: event.target.value } })}
            />
          </label>
        ))}
        <div className="console-stock-qty__field">
          <span className="console-stock-qty__label" aria-hidden="true">
            {packs.length === 0 ? t('stock.quantity.amount') : t('stock.quantity.loose')}
          </span>
          <span className="console-stock-qty__loose">
            <Input
              ref={packs.length === 0 ? firstInputRef : undefined}
              numeric
              className="console-stock-qty__input"
              aria-label={packs.length === 0 ? t('stock.quantity.amountOf', { name: label, unit: format.unitName(entry.unit, item) }) : t('stock.quantity.looseOf', { name: label, unit: format.unitName(entry.unit, item) })}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              value={entry.loose}
              placeholder="0"
              disabled={disabled}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onChange={(event) => onChange({ ...entry, loose: event.target.value })}
            />
            {units.length > 1 ? (
              <Select
                className="console-stock-qty__unit"
                aria-label={t('stock.quantity.unitOf', { name: label })}
                value={entry.unit}
                disabled={disabled}
                onValueChange={(value) => onChange({ ...entry, unit: value as DisplayUnit })}
                options={units.map((unit) => ({ value: unit, label: format.unitName(unit, item) }))}
              />
            ) : (
              <span className="console-stock-qty__unit-word">{format.unitName(entry.unit, item)}</span>
            )}
          </span>
        </div>
      </div>
      {invalid ? (
        <p id={errorId} className="console-stock-qty__error" role="alert">
          {error ?? t('stock.quantity.invalid', { unit: format.unitName(entry.unit, item) })}
        </p>
      ) : !compact && typeof total === 'number' && packs.length > 0 ? (
        <p className="console-stock-qty__total console-num">{t('stock.quantity.total', { total: format.quantity(total, item) })}</p>
      ) : null}
    </div>
  );
}
